"use client";

import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";

import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "@/components/icons/ChevronDownIcon";

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, loop = true, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      loop={loop}
      sideOffset={sideOffset}
      className={cn(
        "z-[200] min-w-[8rem] p-1 overflow-hidden rounded-10 bg-bg-elevated border border-border-default shadow-lg text-fg-default",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    active?: boolean;
  }
>(({ className, active, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    data-active={active}
    className={cn(
      "relative flex gap-2 items-center cursor-default select-none rounded-sm text-sm outline-none transition-colors focus:bg-bg-muted focus:text-fg-default data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "w-full py-2 px-2.5 text-3.5 font-medium text-fg-muted bg-transparent border-none rounded-6 cursor-pointer duration-100 text-left hover:bg-bg-muted hover:text-fg-default [&_svg]:shrink-0",
      "data-[active=true]:text-accent-primary data-[active=true]:bg-accent-primary/10 data-[active=true]:hover:bg-accent-primary/15",
      className
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuIcon = React.forwardRef<
  React.ElementRef<typeof ChevronDownIcon>,
  React.ComponentPropsWithoutRef<typeof ChevronDownIcon>
>(({ className, width = 18, ...props }, ref) => (
  <ChevronDownIcon
    ref={ref}
    width={width}
    className={cn(
      "shrink-0 group-data-[state=open]/trigger:transform-[rotate(180deg)] transition-transform duration-200 opacity-60",
      className
    )}
    {...props}
  />
));
DropdownMenuIcon.displayName = "DropdownMenuIcon";

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-border-subtle", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-3 font-medium text-fg-subtle",
      inset && "pl-8",
      className
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuIcon,
  DropdownMenuSeparator,
  DropdownMenuLabel,
};
